import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// State
import {
  clearStock,
  setAllRewardsInStopList,
  setAllConditionGoodsInStopList,
  reapplyStock,
} from '../../state/modules/stock/actions';
import { showModalConfirm, hideModalConfirm } from '../../state/modules/modalConfirm/actions';
import { getStockConditionGoods, getRewards } from '../../state/modules/stock/selectors';

const hasGoods = goods => Array.isArray(goods) && goods.length > 0;

const isAllItemsInStopList = (goods = []) => {
  if (!hasGoods(goods)) {
    return false;
  }

  return goods.every(good => {
    const variations = good?.variations ?? [];

    if (variations.length === 0) {
      return false;
    }

    return variations.every(variation => variation.is_in_stop_list);
  });
};

export default function useReapplyStock() {
  const dispatch = useDispatch();

  const isOpen = useSelector(state => state.modalConfirm.isOpen);
  const stock_code = useSelector(state => state.stock.data.code);
  const id = useSelector(state => state.order.checkout.currentRestaurant?.id);
  const rewards = useSelector(getRewards);
  const stockConditionGoods = useSelector(getStockConditionGoods);

  const rewardsWithGoods = useMemo(
    () => rewards.filter(reward => hasGoods(reward.goods)),
    [rewards],
  );

  const allRewardsInStopList = useMemo(
    () =>
      rewardsWithGoods.length > 0 &&
      rewardsWithGoods.every(reward => isAllItemsInStopList(reward.goods)),
    [rewardsWithGoods],
  );

  const hasStockConditionGoods = stockConditionGoods.length > 0;
  const allConditionGoodsInStopList = useMemo(
    () => hasStockConditionGoods && isAllItemsInStopList(stockConditionGoods),
    [stockConditionGoods],
  );

  useEffect(() => {
    if (stock_code && id) {
      dispatch(reapplyStock({ stock_code }));
    }
  }, [stock_code, id]);

  // Всплывающие модальные она с оповещением об отсутсвии подарков или товар по условию акции
  const modalConfirmMessageSetter = messageId => ({
    btnConfirmText: <FormattedMessage id="button.delete" />,
    btnCancelText: <FormattedMessage id="button.cancel" />,
    message: <FormattedMessage id={messageId} />,
    onConfirm: () => dispatch(clearStock()),
  });

  useEffect(() => {
    // Оповещение при наличии всех товаров для условия акции в стоп листе, показывает модалку и удаляет акцию
    if (allConditionGoodsInStopList) {
      dispatch(showModalConfirm(modalConfirmMessageSetter('modal.stockNoConditionGoods.text')));
      dispatch(setAllConditionGoodsInStopList(true));
    } else {
      dispatch(setAllConditionGoodsInStopList(false));
    }

    return () => {
      if (isOpen) {
        dispatch(hideModalConfirm());
      }
    };
  }, [allConditionGoodsInStopList]);

  useEffect(() => {
    // Оповещение при наличии всех подарков в стоп листе
    if (allRewardsInStopList) {
      dispatch(showModalConfirm(modalConfirmMessageSetter('modal.stockNoRewards.text')));
      dispatch(setAllRewardsInStopList(true));
    } else {
      dispatch(setAllRewardsInStopList(false));
    }

    return () => {
      if (isOpen) {
        dispatch(hideModalConfirm());
      }
    };
  }, [allRewardsInStopList]);
}
